import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, InputLabel, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import authApi from '~/apis/auth';
import { CloseIcon, EyeIcon, VectorIcon } from '~/assets/icons';
import BaseRow from '~/components/common/base-row/BaseRow';
import { BASE_COLORS } from '~/utils/constants/common';
import { handleApiError, notify } from '~/utils/helper';
import { IResetPassword } from '~/utils/interface/auth';
import authValidation from '~/validation/auth';

type ResetPasswordDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  setOpenBasicInfoUserDialog?: (open: boolean, needRefreshData?: boolean) => void;
};

const initialValues: IResetPassword = {
  old_password: '',
  new_password: '',
};

const ResetPasswordDialog = (props: ResetPasswordDialogProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['admin/layout', 'admin/common']);

  const [showOldPassword, setShowOldPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  const form = useFormik({
    validateOnChange: false,
    validationSchema: authValidation(t).changePassword,
    initialValues,
    onSubmit: async (values) => {
      try {
        await authApi.changePassword({
          old_password: String(values.old_password),
          new_password: String(values.new_password),
        });
        notify('success', t('changePasswordSuccess', { ns: 'admin/common' }));
        handleSetOpen(false, false);
        navigate('/admin/login');
      } catch (e: any) {
        handleApiError(e, true);
      }
    },
  });

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  return (
    <Box className='w-full'>
      <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='xs'>
        <DialogTitle className='bg-[#ED1002]'>
          {t('header.changePassword')}
          <IconButton onClick={() => handleSetOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box component='form'>
            <BaseRow className='gap-[20px] mb-[20px] !items-center'>
              <Box className='w-1/2 font-bold flex items-center'>
                <InputLabel required={true}>{t('header.oldPassword')}:</InputLabel>
              </Box>
              <TextField
                size='medium'
                className='w-1/2'
                name='old_password'
                type={showOldPassword ? 'text' : 'password'}
                placeholder={t('header.oldPassword')}
                value={form.values?.old_password}
                onChange={form.handleChange}
                InputProps={
                  showOldPassword
                    ? {
                        endAdornment: (
                          <VectorIcon
                            className='w-[13px] hover:cursor-pointer hover:opacity-80'
                            fill={BASE_COLORS.RED}
                            onClick={() => setShowOldPassword(!showOldPassword)}
                          />
                        ),
                      }
                    : {
                        endAdornment: (
                          <EyeIcon
                            className='w-[13px] hover:cursor-pointer hover:opacity-80'
                            fill={BASE_COLORS.RED}
                            onClick={() => setShowOldPassword(!showOldPassword)}
                          />
                        ),
                      }
                }
                error={Boolean(form.errors?.old_password)}
                helperText={form.errors?.old_password}
              />
            </BaseRow>
            <BaseRow className='gap-[20px] mb-[20px] !items-center'>
              <Box className='w-1/2 font-bold flex items-center'>
                <InputLabel required={true}>{t('header.newPassword')}:</InputLabel>
              </Box>
              <TextField
                size='medium'
                className='w-1/2'
                name='new_password'
                type={showNewPassword ? 'text' : 'password'}
                placeholder={t('header.newPassword')}
                value={form.values?.new_password}
                onChange={form.handleChange}
                InputProps={
                  showNewPassword
                    ? {
                        endAdornment: (
                          <VectorIcon
                            className='w-[13px] hover:cursor-pointer hover:opacity-80'
                            fill={BASE_COLORS.RED}
                            onClick={() => setShowNewPassword(!showNewPassword)}
                          />
                        ),
                      }
                    : {
                        endAdornment: (
                          <EyeIcon
                            className='w-[13px] hover:cursor-pointer hover:opacity-80'
                            fill='#ED1002'
                            onClick={() => setShowNewPassword(!showNewPassword)}
                          />
                        ),
                      }
                }
                error={Boolean(form.errors?.new_password)}
                helperText={form.errors?.new_password}
              />
            </BaseRow>
            <BaseRow className='gap-[20px] mb-[20px] !items-center'>
              <Box className='w-1/2 font-bold flex items-center'>
                <InputLabel required={true}>{t('header.repeatNewPassword')}:</InputLabel>
              </Box>
              <TextField
                size='medium'
                className='w-1/2'
                name='new_password_confirmation'
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder={t('header.repeatNewPassword')}
                onChange={form.handleChange}
                InputProps={
                  showConfirmPassword
                    ? {
                        endAdornment: (
                          <VectorIcon
                            className='w-[13px] hover:cursor-pointer hover:opacity-80'
                            fill={BASE_COLORS.RED}
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          />
                        ),
                      }
                    : {
                        endAdornment: (
                          <EyeIcon
                            className='w-[13px] hover:cursor-pointer hover:opacity-80'
                            fill='#ED1002'
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          />
                        ),
                      }
                }
                error={Boolean(form.errors?.new_password_confirmation)}
                helperText={form.errors?.new_password_confirmation}
              />
            </BaseRow>

            <Button
              variant='contained'
              color='secondary'
              className='min-w-[125px] flex ml-auto'
              onClick={() => form.handleSubmit()}
            >
              {t('confirm')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default memo(ResetPasswordDialog);
